import React, { createContext, useContext, useEffect, useState } from 'react';
import firebase, { auth, db } from './firebaseConfig';

const AuthContext = createContext({ user: null, loading: true, denied: null });

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [denied, setDenied] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!firebase.apps.length) {
      setLoading(false);
      return undefined;
    }
    const unsub = auth.onAuthStateChanged(async (u) => {
      if (!u) {
        setUser(null);
        setLoading(false);
        return;
      }
      // Allowlist probe: Firestore rules only let allowlisted accounts read
      // config/allowlist, so a permission-denied here means this account is
      // not authorized on this site. Sign them out with a clear message
      // instead of leaving them in a half-working session.
      try {
        await db.collection('config').doc('allowlist').get();
        setDenied(null);
        setUser(u);
      } catch (err) {
        setUser(null);
        setDenied(
          `${u.email || 'This account'} is not authorized on this site. ` +
            'Ask the site owner to add you to the allowlist.'
        );
        auth.signOut().catch(() => {});
      }
      setLoading(false);
    });
    return () => unsub();
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, denied }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

// Firebase Auth errors sometimes surface as raw REST JSON in err.message
// (e.g. {"error":{"code":400,"message":"INVALID_LOGIN_CREDENTIALS",…}}).
// Map those to short copy the login form can show.
const AUTH_MSGS = {
  'auth/invalid-login-credentials': 'Wrong email or password.',
  'auth/invalid-credential': 'Wrong email or password.',
  'auth/user-not-found': 'No account with that email on this site — create one first.',
  'auth/wrong-password': 'Wrong email or password.',
  'auth/email-already-in-use': 'That email already has an account — sign in instead.',
  'auth/weak-password': 'Password must be at least 6 characters.',
  'auth/invalid-email': 'That email address isn’t valid.',
  'auth/too-many-requests': 'Too many attempts — try again in a few minutes.',
  'auth/network-request-failed': 'Network error — check your connection and try again.',
  INVALID_LOGIN_CREDENTIALS: 'Wrong email or password.',
  EMAIL_EXISTS: 'That email already has an account — sign in instead.',
  WEAK_PASSWORD: 'Password must be at least 6 characters.',
  INVALID_EMAIL: 'That email address isn’t valid.',
  TOO_MANY_ATTEMPTS_TRY_LATER: 'Too many attempts — try again in a few minutes.',
  EMAIL_NOT_FOUND: 'No account with that email on this site — create one first.',
};

export function friendlyAuthError(err) {
  if (!err) return 'Something went wrong.';
  const code = err.code || '';
  if (AUTH_MSGS[code]) return AUTH_MSGS[code];
  const raw = String(err.message || '');
  // REST-shaped: {"error":{"message":"INVALID_LOGIN_CREDENTIALS",…}}
  const m = raw.match(/"message"\s*:\s*"([A-Z0-9_]+)"/) || raw.match(/\b([A-Z][A-Z0-9_]{5,})\b/);
  if (m && AUTH_MSGS[m[1]]) return AUTH_MSGS[m[1]];
  if (/INVALID_LOGIN_CREDENTIALS/i.test(raw)) return AUTH_MSGS.INVALID_LOGIN_CREDENTIALS;
  if (/EMAIL_EXISTS/i.test(raw)) return AUTH_MSGS.EMAIL_EXISTS;
  // Strip Firebase's "Firebase: … (auth/…)." wrapper when present.
  const tidy = raw.replace(/^Firebase:\s*/i, '').replace(/\s*\(auth\/[^)]+\)\.?$/, '').trim();
  return tidy || 'Something went wrong.';
}

export function signUp(email, password) {
  return auth.createUserWithEmailAndPassword(email, password);
}

export function signIn(email, password) {
  return auth.signInWithEmailAndPassword(email, password);
}

export function resetPassword(email) {
  // Continue back to this site's login after the Firebase-hosted reset form.
  const continueUrl = `${window.location.origin}/login`;
  return auth.sendPasswordResetEmail(email, { url: continueUrl, handleCodeInApp: false });
}

export function signOut() {
  return auth.signOut();
}
